import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Swal from 'sweetalert2';
import {
  FaArrowRight,
  FaBan,
  FaCashRegister,
  FaClipboardCheck,
  FaHandHoldingUsd,
  FaLayerGroup,
  FaSignInAlt,
  FaUserTie,
  FaUsers,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import {
  getAdminActorType,
  hasAnyAllowedPath,
  isKaryawanUser,
  readStoredAdminUser,
} from './adminAccessControl';
import './FitnessTransactionsManager.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DAILY_MEMBER_ID = 39;

const SALES_TAB_DEFINITIONS = [
  {
    key: 'membership',
    label: 'Membership',
    icon: FaClipboardCheck,
    description: 'Daily pass dan membership bulanan dalam satu alur singkat.',
    requiredPaths: ['/membership-payment'],
  },
  {
    key: 'class',
    label: 'Class',
    icon: FaUsers,
    description: 'Input kelas, jumlah peserta, dan metode bayar dari front desk.',
    requiredPaths: ['/fitness-transactions'],
  },
  {
    key: 'pt',
    label: 'PT',
    icon: FaUserTie,
    description: 'Jual paket PT atau per sesi sesuai perjanjian trainer.',
    requiredPaths: ['/fitness-transactions'],
  },
  {
    key: 'checkin',
    label: 'Check-in',
    icon: FaSignInAlt,
    description: 'Bantu member check-in dari meja FO tanpa pindah halaman.',
    requiredPaths: ['/membership-payment'],
  },
];

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const frontDeskTimezoneSuffixPattern = /\s*(?:UTC|GMT|WIB|WITA|WIT|[+-]\d{2}:?\d{2}|Z)$/i;
const frontDeskUTCStyleSuffixPattern = /\s*(?:UTC|GMT|[+-]00:?(?:00)?|Z)$/i;

const parseFrontDeskDateTime = value => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const parseAsLocalWallClock = input => {
    const normalized = input
      .replace(frontDeskTimezoneSuffixPattern, '')
      .replace(' ', 'T');
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/
    );

    if (!match) {
      return null;
    }

    const [, year, month, day, hour, minute, second = '0'] = match;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const looksLikeDateTime = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw);
  if (looksLikeDateTime && (!frontDeskTimezoneSuffixPattern.test(raw) || frontDeskUTCStyleSuffixPattern.test(raw))) {
    return parseAsLocalWallClock(raw);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return parseAsLocalWallClock(raw);
  }

  return parsed;
};

const formatDateTime = value => {
  const date = parseFrontDeskDateTime(value);
  if (!date) {
    return '-';
  }
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFrontDeskTransactionDateValue = item =>
  parseFrontDeskDateTime(item?.created_at || item?.paid_at || item?.updated_at || item?.date);

const sortFrontDeskTransactionsNewestFirst = rows =>
  [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
    const leftDate = getFrontDeskTransactionDateValue(left);
    const rightDate = getFrontDeskTransactionDateValue(right);
    const leftTime = leftDate ? leftDate.getTime() : 0;
    const rightTime = rightDate ? rightDate.getTime() : 0;

    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return Number(right?.id || 0) - Number(left?.id || 0);
  });

const formatDateInputValue = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayDateInputValue = () => formatDateInputValue(new Date());

const isApiSuccess = data =>
  data && (data.error === 0 || data.error === '0' || typeof data.error === 'undefined');

const memberSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '50px',
    borderRadius: '16px',
    borderColor: state.isFocused ? 'rgba(132, 175, 61, 0.45)' : 'rgba(24, 38, 30, 0.1)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(132, 175, 61, 0.38)',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0.18rem 0.65rem',
  }),
  placeholder: base => ({
    ...base,
    color: '#728273',
  }),
  singleValue: base => ({
    ...base,
    color: '#1d3024',
  }),
  input: base => ({
    ...base,
    color: '#1d3024',
  }),
  menu: base => ({
    ...base,
    zIndex: 30,
    borderRadius: '18px',
    overflow: 'hidden',
    border: '1px solid rgba(24, 38, 30, 0.08)',
    boxShadow: '0 24px 48px rgba(15, 30, 18, 0.12)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgba(176, 223, 92, 0.26)'
      : state.isFocused
        ? 'rgba(176, 223, 92, 0.16)'
        : '#fff',
    color: '#1d3024',
    padding: '0.82rem 0.95rem',
  }),
  noOptionsMessage: base => ({
    ...base,
    color: '#617067',
  }),
  loadingMessage: base => ({
    ...base,
    color: '#617067',
  }),
};

const formatMemberFullName = member =>
  [member?.first_name, member?.last_name].filter(Boolean).join(' ').trim() || `Member #${member?.id || '-'}`;

const formatMemberSearchLabel = member => {
  const name = formatMemberFullName(member);
  const contact = [member?.hp, member?.email, member?.identity_no].filter(Boolean).join(' • ');
  return contact ? `${name} • ${contact}` : name;
};

const buildMemberOption = member => ({
  value: String(member?.id ?? ''),
  label: formatMemberSearchLabel(member),
  data: member,
});

const getMembershipStatusConfig = status => {
  switch (Number(status || 0)) {
    case 1:
      return { label: 'Completed', className: 'is-completed' };
    case 2:
      return { label: 'Failed', className: 'is-failed' };
    case 3:
      return { label: 'Cancelled', className: 'is-cancelled' };
    default:
      return { label: 'Pending', className: 'is-pending' };
  }
};

const getFitnessTransactionStatusConfig = status => {
  switch (String(status || '').trim().toLowerCase()) {
    case 'cancelled':
      return { label: 'Cancelled', className: 'is-cancelled' };
    default:
      return { label: 'Posted', className: 'is-completed' };
  }
};

const getFitnessTransactionTypeKey = item =>
  String(item?.transaction_type || '').trim().toLowerCase();

const isClassFitnessTransaction = item =>
  getFitnessTransactionTypeKey(item) === 'class_sale';

const isPTFitnessTransaction = item =>
  ['pt_package_sale', 'pt_session_sale'].includes(getFitnessTransactionTypeKey(item));

const getFitnessTransactionChannelLabel = item => {
  switch (getFitnessTransactionTypeKey(item)) {
    case 'class_sale':
      return 'Class sale';
    case 'pt_session_sale':
      return 'PT session sale';
    case 'pt_package_sale':
      return 'PT package sale';
    default:
      return 'Fitness sale';
  }
};

const getPTSaleStatusConfig = status => {
  switch (String(status || '').trim().toLowerCase()) {
    case 'paid':
      return { label: 'paid', className: 'is-completed' };
    case 'cancelled':
      return { label: 'cancelled', className: 'is-cancelled' };
    case 'failed':
    case 'refunded':
      return { label: status || '-', className: 'is-failed' };
    default:
      return { label: status || 'pending', className: 'is-pending' };
  }
};

function FitnessTransactionsManager() {
  const token = useMemo(() => localStorage.getItem('sessionToken') || '', []);
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const actorType = useMemo(() => getAdminActorType(currentUser), [currentUser]);
  const isScopedActor = actorType === 'outlet' || actorType === 'karyawan';
  const isStaffUser = isKaryawanUser(currentUser);
  const canUseMembershipSales = hasAnyAllowedPath('/membership-payment', currentUser);
  const canUseFitnessSales = hasAnyAllowedPath('/fitness-transactions', currentUser);
  const canViewShareBreakdown = !isStaffUser;
  const [searchParams, setSearchParams] = useSearchParams();

  const availableTabs = useMemo(
    () =>
      SALES_TAB_DEFINITIONS.filter(tab =>
        tab.requiredPaths.some(path => hasAnyAllowedPath(path, currentUser))
      ),
    [currentUser]
  );

  const currentTabParam = searchParams.get('tab');
  const requestedOutletParam = searchParams.get('outlet_id');
  const requestedMemberParam = searchParams.get('member_id');
  const requestedMemberNameParam = searchParams.get('member_name');
  const activeTab =
    availableTabs.find(tab => tab.key === currentTabParam)?.key || availableTabs[0]?.key || '';

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(
    isScopedActor ? String(currentUser.outlet_id || '') : ''
  );
  const [activityDateRange, setActivityDateRange] = useState(() => {
    const today = getTodayDateInputValue();
    return {
      dateFrom: today,
      dateTo: today,
    };
  });
  const [members, setMembers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [classes, setClasses] = useState([]);
  const [ptUsers, setPTUsers] = useState([]);
  const [ptClients, setPTClients] = useState([]);
  const [ptProducts, setPTProducts] = useState([]);
  const [ptSales, setPTSales] = useState([]);
  const [membershipPayments, setMembershipPayments] = useState([]);
  const [memberPaymentHistory, setMemberPaymentHistory] = useState([]);
  const [latestCheckinMembershipPreview, setLatestCheckinMembershipPreview] = useState(null);
  const [fitnessTransactions, setFitnessTransactions] = useState([]);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckinSubmitting, setIsCheckinSubmitting] = useState(false);
  const [membershipStatusUpdatingId, setMembershipStatusUpdatingId] = useState('');
  const [selectedMembershipMemberOption, setSelectedMembershipMemberOption] = useState(null);
  const [selectedCheckinMemberOption, setSelectedCheckinMemberOption] = useState(null);

  const [membershipForm, setMembershipForm] = useState({
    member_id: '',
    membership_id: '',
    payment_method: '',
    quantity: '1',
  });

  const [classSaleForm, setClassSaleForm] = useState({
    class_id: '',
    participant_count: '1',
    payment_method: '',
    notes: '',
  });

  const [ptSaleForm, setPTSaleForm] = useState({
    pt_client_id: '',
    pt_user_id: '',
    product_id: '',
    payment_method: '',
    discount_amount: '',
    notes: '',
  });

  const handleActivityDateChange = useCallback((field, value) => {
    setActivityDateRange(prev => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (next.dateFrom && next.dateTo && next.dateFrom > next.dateTo) {
        if (field === 'dateFrom') {
          next.dateTo = value;
        } else {
          next.dateFrom = value;
        }
      }

      return next;
    });
  }, []);

  const resetActivityDateRangeToToday = useCallback(() => {
    const today = getTodayDateInputValue();
    setActivityDateRange({
      dateFrom: today,
      dateTo: today,
    });
  }, []);

  const parseApiResponse = useCallback(async response => {
    const raw = await response.text();
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return { error: 1, message: raw };
    }
  }, []);

  const performRequest = useCallback(
    async (url, options, fallbackMessage) => {
      const response = await fetch(url, options);
      const data = await parseApiResponse(response);
      if (!response.ok || !isApiSuccess(data)) {
        throw new Error(data.message || fallbackMessage);
      }
      return data;
    },
    [parseApiResponse]
  );

  const fetchOutlets = useCallback(async () => {
    if (isScopedActor) {
      const scopedOutlet = currentUser.outlet_id
        ? [
            {
              id: String(currentUser.outlet_id),
              outlet_name: currentUser.outlet_name || 'Assigned outlet',
            },
          ]
        : [];
      setOutlets(scopedOutlet);
      if (scopedOutlet[0]?.id) {
        setSelectedOutlet(prev => prev || scopedOutlet[0].id);
      }
      return;
    }

    const data = await performRequest(
      `${API_BASE_URL}outletlist`,
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ limit: 200, offset: 0 }),
      },
      'Gagal mengambil daftar outlet'
    );

    const normalized = (Array.isArray(data.results) ? data.results : []).map(item => ({
      id: String(item.id ?? item.ID ?? ''),
      outlet_name:
        item.outlet_name || item.OutletName || item.name || `Outlet #${item.id || item.ID}`,
    }));
    setOutlets(normalized);
    if (normalized[0]?.id) {
      setSelectedOutlet(prev => prev || normalized[0].id);
    }
  }, [authHeaders, currentUser, isScopedActor, performRequest]);

  const fetchPaymentAccounts = useCallback(async () => {
    const data = await performRequest(
      `${API_BASE_URL}/getassetaccounts`,
      {
        method: 'POST',
        headers: authHeaders,
      },
      'Gagal mengambil metode pembayaran'
    );

    setPaymentAccounts(
      (Array.isArray(data.results) ? data.results : []).map(item => ({
        id: String(item.account_id ?? item.AccountID ?? item.id ?? ''),
        name: item.account_name || item.AccountName || item.accountname || '',
      }))
    );
  }, [authHeaders, performRequest]);

  const fetchMembers = useCallback(
    async (outletId, search = '') => {
      if (!canUseMembershipSales || !outletId) {
        if (!search) {
          setMembers([]);
        }
        return [];
      }

      const data = await performRequest(
        `${API_BASE_URL}member/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: search.trim() ? 50 : 80,
            offset: 0,
            outlet: Number(outletId),
            search: search.trim(),
          }),
        },
        'Gagal mengambil daftar member'
      );
      const nextMembers = Array.isArray(data.results) ? data.results : [];
      if (!search.trim()) {
        setMembers(nextMembers);
      }
      return nextMembers;
    },
    [authHeaders, canUseMembershipSales, performRequest]
  );

  const fetchMemberships = useCallback(
    async outletId => {
      if (!canUseMembershipSales || !outletId) {
        setMemberships([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}membership/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ limit: 200, offset: 0, outlet: Number(outletId) }),
        },
        'Gagal mengambil daftar membership'
      );
      setMemberships(Array.isArray(data.results) ? data.results : []);
    },
    [authHeaders, canUseMembershipSales, performRequest]
  );

  const fetchMembershipActivity = useCallback(
    async outletId => {
      if (!canUseMembershipSales || !outletId) {
        setMembershipPayments([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}listmembershippayments`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 15,
            offset: 0,
            outlet: Number(outletId),
            date_from: activityDateRange.dateFrom,
            date_to: activityDateRange.dateTo,
          }),
        },
        'Gagal mengambil riwayat membership'
      );
      setMembershipPayments(Array.isArray(data.results) ? data.results : []);
    },
    [activityDateRange.dateFrom, activityDateRange.dateTo, authHeaders, canUseMembershipSales, performRequest]
  );

  const fetchMemberPaymentHistory = useCallback(
    async (outletId, memberId) => {
      if (!canUseMembershipSales || !outletId || !memberId) {
        setMemberPaymentHistory([]);
        return [];
      }

      const data = await performRequest(
        `${API_BASE_URL}listmembershippayments`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 15,
            offset: 0,
            outlet: Number(outletId),
            member_id: Number(memberId),
            date_from: activityDateRange.dateFrom,
            date_to: activityDateRange.dateTo,
          }),
        },
        'Gagal mengambil history membership member'
      );
      const results = Array.isArray(data.results) ? data.results : [];
      setMemberPaymentHistory(results);
      return results;
    },
    [activityDateRange.dateFrom, activityDateRange.dateTo, authHeaders, canUseMembershipSales, performRequest]
  );

  const fetchLatestCheckinMembershipPreview = useCallback(
    async (outletId, memberId) => {
      if (!canUseMembershipSales || !outletId || !memberId) {
        return null;
      }

      const data = await performRequest(
        `${API_BASE_URL}membership-payment/latest`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet: Number(outletId),
            member_id: Number(memberId),
          }),
        },
        'Gagal mengambil membership terakhir member'
      );

      const result = data.results && typeof data.results === 'object' ? data.results : null;
      return result;
    },
    [authHeaders, canUseMembershipSales, performRequest]
  );

  const fetchClasses = useCallback(
    async outletId => {
      if (!canUseFitnessSales || !outletId) {
        setClasses([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}fitness-class/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            outlet_id: Number(outletId),
            is_active: true,
          }),
        },
        'Gagal mengambil daftar class'
      );
      setClasses(Array.isArray(data.results) ? data.results : []);
    },
    [authHeaders, canUseFitnessSales, performRequest]
  );

  const fetchPTUsers = useCallback(async () => {
    if (!canUseFitnessSales) {
      setPTUsers([]);
      return;
    }

    const data = await performRequest(
      `${API_BASE_URL}pt-user/list`,
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ limit: 200, offset: 0 }),
      },
      'Gagal mengambil daftar PT'
    );
    setPTUsers(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, canUseFitnessSales, performRequest]);

  const fetchPTClients = useCallback(
    async outletId => {
      if (!canUseFitnessSales || !outletId) {
        setPTClients([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}pt-client/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            outlet_id: Number(outletId),
            is_active: true,
          }),
        },
        'Gagal mengambil daftar PT client'
      );
      setPTClients(Array.isArray(data.results) ? data.results : []);
    },
    [authHeaders, canUseFitnessSales, performRequest]
  );

  const fetchPTProducts = useCallback(
    async outletId => {
      if (!canUseFitnessSales || !outletId) {
        setPTProducts([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}pt-product/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            outlet_id: Number(outletId),
            is_active: true,
          }),
        },
        'Gagal mengambil daftar PT product'
      );
      setPTProducts(Array.isArray(data.results) ? data.results : []);
    },
    [authHeaders, canUseFitnessSales, performRequest]
  );

  const fetchPTSales = useCallback(
    async outletId => {
      if (!canUseFitnessSales || !outletId) {
        setPTSales([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}pt-sale/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 20,
            offset: 0,
            outlet_id: Number(outletId),
            date_from: activityDateRange.dateFrom,
            date_to: activityDateRange.dateTo,
          }),
        },
        'Gagal mengambil daftar PT sale'
      );
      setPTSales(Array.isArray(data.results) ? data.results : []);
    },
    [activityDateRange.dateFrom, activityDateRange.dateTo, authHeaders, canUseFitnessSales, performRequest]
  );

  const fetchFitnessActivity = useCallback(
    async outletId => {
      if (!canUseFitnessSales || !outletId) {
        setFitnessTransactions([]);
        return;
      }

      const data = await performRequest(
        `${API_BASE_URL}fitness-transaction/list`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 10,
            offset: 0,
            outlet_id: Number(outletId),
            date_from: activityDateRange.dateFrom,
            date_to: activityDateRange.dateTo,
          }),
        },
        'Gagal mengambil transaksi fitness'
      );
      setFitnessTransactions(Array.isArray(data.results) ? data.results : []);
    },
    [activityDateRange.dateFrom, activityDateRange.dateTo, authHeaders, canUseFitnessSales, performRequest]
  );

  const loadMemberOptions = useCallback(
    async inputValue => {
      try {
        const results = await fetchMembers(selectedOutlet, inputValue);
        return results.map(buildMemberOption);
      } catch (error) {
        console.error('Gagal mencari member:', error);
        return [];
      }
    },
    [fetchMembers, selectedOutlet]
  );

  const refreshMembershipData = useCallback(
    async (outletId, memberId = '') => {
      await Promise.all([
        fetchMembershipActivity(outletId),
        memberId ? fetchMemberPaymentHistory(outletId, memberId) : Promise.resolve(),
      ]);
    },
    [fetchMemberPaymentHistory, fetchMembershipActivity]
  );

  useEffect(() => {
    if (!token) {
      window.location.href = '/loginadmin';
      return;
    }

    setIsBootstrapping(true);
    let active = true;

    const initialize = async () => {
      try {
        await Promise.all([
          fetchOutlets(),
          fetchPaymentAccounts(),
          canUseFitnessSales ? fetchPTUsers() : Promise.resolve(),
        ]);
      } catch (error) {
        if (active) {
          Swal.fire('Error', error.message || 'Gagal memuat data awal', 'error');
        }
      } finally {
        if (active) {
          setIsBootstrapping(false);
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canUseFitnessSales, fetchOutlets, fetchPTUsers, fetchPaymentAccounts, token]);

  useEffect(() => {
    if (!availableTabs.length) {
      return;
    }
    if (!availableTabs.some(tab => tab.key === currentTabParam)) {
      setSearchParams({ tab: availableTabs[0].key }, { replace: true });
    }
  }, [availableTabs, currentTabParam, setSearchParams]);

  useEffect(() => {
    if (!selectedOutlet) {
      return;
    }

    const loadOutletData = async () => {
      try {
        await Promise.all([
          canUseMembershipSales
            ? Promise.all([
                fetchMembers(selectedOutlet),
                fetchMemberships(selectedOutlet),
                fetchMembershipActivity(selectedOutlet),
              ])
            : Promise.resolve(),
          canUseFitnessSales
            ? Promise.all([
                fetchClasses(selectedOutlet),
                fetchFitnessActivity(selectedOutlet),
                fetchPTClients(selectedOutlet),
                fetchPTProducts(selectedOutlet),
                fetchPTSales(selectedOutlet),
              ])
            : Promise.resolve(),
        ]);
      } catch (error) {
        Swal.fire('Error', error.message || 'Gagal memuat data transaksi', 'error');
      }
    };

    loadOutletData();
  }, [
    canUseFitnessSales,
    canUseMembershipSales,
    fetchClasses,
    fetchFitnessActivity,
    fetchPTClients,
    fetchPTProducts,
    fetchPTSales,
    fetchMembers,
    fetchMembershipActivity,
    fetchMemberships,
    selectedOutlet,
  ]);

  useEffect(() => {
    setSelectedMembershipMemberOption(null);
    setSelectedCheckinMemberOption(null);
    setMemberPaymentHistory([]);
    setMembershipForm(prev => ({
      ...prev,
      member_id: '',
      membership_id: '',
      quantity: '1',
    }));
  }, [selectedOutlet]);

  useEffect(() => {
    if (activeTab !== 'membership') {
      return;
    }

    const requestedOutletID = String(requestedOutletParam || '').trim();
    if (!requestedOutletID || String(selectedOutlet) === requestedOutletID) {
      return;
    }

    setSelectedOutlet(requestedOutletID);
  }, [activeTab, requestedOutletParam, selectedOutlet]);

  const defaultMemberOptions = useMemo(
    () => members.map(buildMemberOption),
    [members]
  );

  useEffect(() => {
    if (activeTab !== 'membership') {
      return;
    }

    const requestedMemberID = String(requestedMemberParam || '').trim();
    if (!requestedMemberID || !selectedOutlet) {
      return;
    }

    const requestedOutletID = String(requestedOutletParam || '').trim();
    if (requestedOutletID && String(selectedOutlet) !== requestedOutletID) {
      return;
    }

    const matchedMember = members.find(item => String(item.id) === requestedMemberID);
    const fallbackName = String(requestedMemberNameParam || '').trim();
    const nextOption = matchedMember
      ? buildMemberOption(matchedMember)
      : {
          value: requestedMemberID,
          label: fallbackName || `Member #${requestedMemberID}`,
          data: fallbackName
            ? { id: Number(requestedMemberID), first_name: fallbackName, last_name: '' }
            : { id: Number(requestedMemberID) },
        };

    if (
      selectedMembershipMemberOption?.value !== nextOption.value ||
      membershipForm.member_id !== nextOption.value
    ) {
      setSelectedMembershipMemberOption(nextOption);
      setMembershipForm(prev => ({
        ...prev,
        member_id: nextOption.value,
      }));
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('member_id');
    nextParams.delete('member_name');
    nextParams.delete('outlet_id');
    setSearchParams(nextParams, { replace: true });
  }, [
    activeTab,
    members,
    membershipForm.member_id,
    requestedMemberNameParam,
    requestedMemberParam,
    requestedOutletParam,
    searchParams,
    selectedMembershipMemberOption,
    selectedOutlet,
    setSearchParams,
  ]);

  const focusedMemberId = useMemo(() => {
    if (activeTab === 'checkin') {
      return selectedCheckinMemberOption?.value || '';
    }
    if (activeTab === 'membership') {
      return selectedMembershipMemberOption?.value || membershipForm.member_id || '';
    }
    return '';
  }, [
    activeTab,
    membershipForm.member_id,
    selectedCheckinMemberOption,
    selectedMembershipMemberOption,
  ]);

  useEffect(() => {
    if (!canUseMembershipSales || !selectedOutlet || !focusedMemberId) {
      setMemberPaymentHistory([]);
      return;
    }

    let active = true;

    const loadFocusedMemberHistory = async () => {
      try {
        const results = await fetchMemberPaymentHistory(selectedOutlet, focusedMemberId);
        if (!active) {
          return;
        }
        setMemberPaymentHistory(results);
      } catch (error) {
        if (active) {
          console.error('Gagal memuat history member terpilih:', error);
          setMemberPaymentHistory([]);
        }
      }
    };

    loadFocusedMemberHistory();

    return () => {
      active = false;
    };
  }, [canUseMembershipSales, fetchMemberPaymentHistory, focusedMemberId, selectedOutlet]);

  useEffect(() => {
    const selectedCheckinMemberId = selectedCheckinMemberOption?.value || '';

    if (!canUseMembershipSales || !selectedOutlet || !selectedCheckinMemberId) {
      setLatestCheckinMembershipPreview(null);
      return;
    }

    let active = true;

    const loadLatestMembershipPreview = async () => {
      try {
        const result = await fetchLatestCheckinMembershipPreview(selectedOutlet, selectedCheckinMemberId);
        if (!active) {
          return;
        }
        setLatestCheckinMembershipPreview(result);
      } catch (error) {
        if (active) {
          console.error('Gagal memuat preview membership terakhir member:', error);
          setLatestCheckinMembershipPreview(null);
        }
      }
    };

    loadLatestMembershipPreview();

    return () => {
      active = false;
    };
  }, [canUseMembershipSales, fetchLatestCheckinMembershipPreview, selectedCheckinMemberOption, selectedOutlet]);

  const selectedOutletName =
    outlets.find(item => String(item.id) === String(selectedOutlet))?.outlet_name || 'Outlet belum dipilih';
  const isActivityDateRangeToday =
    activityDateRange.dateFrom === getTodayDateInputValue() &&
    activityDateRange.dateTo === getTodayDateInputValue();

  const selectedMembershipMember = useMemo(
    () => selectedMembershipMemberOption?.data || null,
    [selectedMembershipMemberOption]
  );
  const selectedCheckinMember = useMemo(
    () => selectedCheckinMemberOption?.data || null,
    [selectedCheckinMemberOption]
  );

  const selectedMembership = useMemo(
    () => memberships.find(item => String(item.id) === String(membershipForm.membership_id)),
    [membershipForm.membership_id, memberships]
  );
  const isSelectedMembershipDaily = useMemo(() => {
    const membershipType = String(selectedMembership?.type || '').trim().toLowerCase();
    const membershipName = String(
      selectedMembership?.membership_name || selectedMembership?.name || ''
    )
      .trim()
      .toLowerCase();
    return membershipType === 'daily' || membershipName.includes('harian');
  }, [selectedMembership]);
  const membershipQuantity = useMemo(
    () => Math.max(Number(membershipForm.quantity || 1), 1),
    [membershipForm.quantity]
  );
  const selectedClass = useMemo(
    () => classes.find(item => String(item.id) === String(classSaleForm.class_id)),
    [classSaleForm.class_id, classes]
  );
  const selectedPT = useMemo(
    () => ptUsers.find(item => String(item.id) === String(ptSaleForm.pt_user_id)),
    [ptSaleForm.pt_user_id, ptUsers]
  );
  const selectedPTClient = useMemo(
    () => ptClients.find(item => String(item.id) === String(ptSaleForm.pt_client_id)),
    [ptClients, ptSaleForm.pt_client_id]
  );
  const selectedPTProduct = useMemo(
    () => ptProducts.find(item => String(item.id) === String(ptSaleForm.product_id)),
    [ptProducts, ptSaleForm.product_id]
  );
  const availablePTProducts = useMemo(
    () =>
      ptProducts.filter(
        item =>
          item.is_active &&
          ptSaleForm.pt_user_id &&
          String(item.pt_user_id) === String(ptSaleForm.pt_user_id)
      ),
    [ptProducts, ptSaleForm.pt_user_id]
  );

  useEffect(() => {
    if (!ptSaleForm.product_id) {
      return;
    }

    const stillValid = availablePTProducts.some(
      item => String(item.id) === String(ptSaleForm.product_id)
    );
    if (!stillValid) {
      setPTSaleForm(prev => ({ ...prev, product_id: '' }));
    }
  }, [availablePTProducts, ptSaleForm.product_id]);

  useEffect(() => {
    if (!isSelectedMembershipDaily) {
      setMembershipForm(prev =>
        prev.quantity === '1' ? prev : { ...prev, quantity: '1' }
      );
      return;
    }

    setSelectedMembershipMemberOption(null);
    setMembershipForm(prev => {
      const nextQuantity = Number(prev.quantity || 0) > 0 ? prev.quantity : '1';
      if (prev.member_id === '' && prev.quantity === nextQuantity) {
        return prev;
      }
      return {
        ...prev,
        member_id: '',
        quantity: nextQuantity,
      };
    });
  }, [isSelectedMembershipDaily]);

  const membershipPreview = useMemo(
    () => ({
      amount: Number(selectedMembership?.price || 0),
      duration: Number(selectedMembership?.expired || 0),
      label: selectedMembership?.membership_name || selectedMembership?.name || '-',
      quantity: isSelectedMembershipDaily ? membershipQuantity : 1,
      totalAmount:
        Number(selectedMembership?.price || 0) * (isSelectedMembershipDaily ? membershipQuantity : 1),
    }),
    [isSelectedMembershipDaily, membershipQuantity, selectedMembership]
  );

  const classPreview = useMemo(() => {
    const participantCount = Number(classSaleForm.participant_count || 0);
    const grossAmount = Number(selectedClass?.unit_price || 0) * participantCount;
    return {
      grossAmount,
      pricePerParticipant: Number(selectedClass?.unit_price || 0),
    };
  }, [classSaleForm.participant_count, selectedClass]);

  const ptPreview = useMemo(() => {
    const sessionCount = Number(selectedPTProduct?.session_count || 0);
    const listedPrice = Number(selectedPTProduct?.price || 0);
    const discountAmount = Number(ptSaleForm.discount_amount || 0);
    const grossAmount = Math.max(listedPrice - discountAmount, 0);
    return {
      grossAmount,
      listedPrice,
      discountAmount,
      sessionCount,
    };
  }, [
    ptSaleForm.discount_amount,
    selectedPTProduct,
  ]);

  const selectedPaymentAccount = useMemo(
    () => paymentAccounts.find(account => String(account.id) === String(membershipForm.payment_method)),
    [membershipForm.payment_method, paymentAccounts]
  );
  const selectedPTPaymentAccount = useMemo(
    () => paymentAccounts.find(account => String(account.id) === String(ptSaleForm.payment_method)),
    [paymentAccounts, ptSaleForm.payment_method]
  );
  const membershipActivityRows = useMemo(
    () => sortFrontDeskTransactionsNewestFirst(focusedMemberId ? memberPaymentHistory : membershipPayments),
    [focusedMemberId, memberPaymentHistory, membershipPayments]
  );

  const ptSalesByFitnessTransactionId = useMemo(() => {
    const lookup = new Map();
    ptSales.forEach(item => {
      const fitnessTxnID = item?.fitness_transaction_id;
      if (fitnessTxnID) {
        lookup.set(String(fitnessTxnID), item);
      }
    });
    return lookup;
  }, [ptSales]);

  const recentFitnessActivityRows = useMemo(
    () =>
      sortFrontDeskTransactionsNewestFirst(
        fitnessTransactions.map(item => ({
          ...item,
          linked_pt_sale: ptSalesByFitnessTransactionId.get(String(item.id)) || null,
        }))
      ),
    [fitnessTransactions, ptSalesByFitnessTransactionId]
  );

  const latestFocusedMemberPayment = useMemo(
    () =>
      activeTab === 'checkin'
        ? latestCheckinMembershipPreview || membershipActivityRows[0] || null
        : focusedMemberId
          ? membershipActivityRows[0] || null
          : null,
    [activeTab, focusedMemberId, latestCheckinMembershipPreview, membershipActivityRows]
  );

  const summaryCards = useMemo(() => {
    const cards = [
      {
        label: 'Flow aktif',
        value: availableTabs.length,
        note: 'Tab transaksi yang aktif untuk akun ini',
      },
      {
        label: 'Metode bayar',
        value: paymentAccounts.length,
        note: 'Cash, QRIS, transfer, dan akun asset lain',
      },
    ];

    if (activeTab === 'membership') {
      cards.push({
        label: 'Plan tersedia',
        value: memberships.length,
        note: 'Paket membership aktif untuk outlet ini',
      });
    } else if (activeTab === 'checkin') {
      cards.push({
        label: 'Queue validasi',
        value: membershipPayments.length,
        note: 'Riwayat membership terbaru untuk outlet ini',
      });
    } else if (activeTab === 'class') {
      cards.push({
        label: 'Class aktif',
        value: classes.length,
        note: 'Hanya class aktif yang tampil untuk staf',
      });
    } else if (activeTab === 'pt') {
      cards.push({
        label: 'PT catalog',
        value: ptProducts.filter(item => item.is_active).length,
        note: 'Produk PT aktif yang dipakai sales desk',
      });
    }

    return cards;
  }, [
    activeTab,
    availableTabs.length,
    classes.length,
    membershipPayments.length,
    memberships.length,
    paymentAccounts.length,
    ptProducts,
  ]);

  const handleMembershipSale = async event => {
    event.preventDefault();

    if (
      !membershipForm.membership_id ||
      !membershipForm.payment_method ||
      (!isSelectedMembershipDaily && !membershipForm.member_id)
    ) {
      Swal.fire('Warning', 'Lengkapi member, paket, dan metode pembayaran terlebih dahulu.', 'warning');
      return;
    }

    const quantity = isSelectedMembershipDaily ? membershipQuantity : 1;
    setIsSubmitting(true);
    try {
      const data = await performRequest(
        `${API_BASE_URL}upgradememberadmin`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet: Number(selectedOutlet),
            member_id: Number(isSelectedMembershipDaily ? DAILY_MEMBER_ID : membershipForm.member_id),
            membership_id: Number(membershipForm.membership_id),
            payment_methode: membershipForm.payment_method,
            transaction_source: isSelectedMembershipDaily ? 'daily_pass' : 'membership_sale',
            qty: quantity,
          }),
        },
        'Gagal memproses penjualan membership'
      );

      const totalAmount = Number(data.total_amount ?? data.amount ?? 0);

      Swal.fire({
        icon: 'success',
        title: isSelectedMembershipDaily ? 'Member harian berhasil dipost' : 'Transaksi membership dibuat',
        html: `
          <strong>Total: ${formatCurrency(totalAmount)}</strong><br/>
          ${isSelectedMembershipDaily ? `Jumlah orang: <strong>${quantity}</strong><br/>` : ''}
          Expired sampai: <strong>${formatDateTime(data.expired)}</strong><br/>
          Status awal: <strong>Pending</strong>
        `,
      });

      setMembershipForm(prev => ({
        ...prev,
        membership_id: '',
        quantity: '1',
      }));
      await refreshMembershipData(
        selectedOutlet,
        isSelectedMembershipDaily ? '' : selectedMembershipMemberOption?.value || membershipForm.member_id
      );
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memproses penjualan membership', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMembershipStatusChange = async (paymentId, currentStatus, nextStatus) => {
    if (Number(currentStatus) === 3) {
      Swal.fire('Info', 'Transaksi yang sudah dibatalkan tidak bisa diubah lagi.', 'info');
      return;
    }
    if (
      Number(currentStatus) === 1 &&
      Number(nextStatus) !== 1 &&
      Number(nextStatus) !== 3
    ) {
      Swal.fire('Info', 'Transaksi yang sudah completed hanya bisa di-void.', 'info');
      return;
    }

    if (Number(currentStatus) === Number(nextStatus)) {
      return;
    }

    let cancelReason = '';
    if (Number(nextStatus) === 3) {
      const isVoidFromCompleted = Number(currentStatus) === 1;
      const confirm = await Swal.fire({
        title: isVoidFromCompleted ? 'Void transaksi membership?' : 'Batalkan transaksi membership?',
        text: isVoidFromCompleted
          ? 'Jurnal transaksi akan dibalik dan membership ini tidak lagi dianggap aktif.'
          : 'Transaksi pending ini akan ditandai cancelled dan tidak bisa diaktifkan lagi.',
        icon: 'warning',
        showCancelButton: true,
        input: 'text',
        inputPlaceholder: isVoidFromCompleted ? 'Alasan void' : 'Alasan pembatalan',
        confirmButtonText: isVoidFromCompleted ? 'Void' : 'Batalkan',
      });
      if (!confirm.isConfirmed) {
        return;
      }
      cancelReason = confirm.value || '';
    }

    setMembershipStatusUpdatingId(String(paymentId));
    try {
      await performRequest(
        `${API_BASE_URL}updatemembershippayment`,
        {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({
            id: Number(paymentId),
            status: Number(nextStatus),
            cancel_reason: cancelReason,
          }),
        },
        'Gagal memperbarui status membership'
      );

      await refreshMembershipData(selectedOutlet, focusedMemberId);
      Swal.fire(
        'Success',
        Number(nextStatus) === 1
          ? 'Transaksi membership diselesaikan dan jurnal sudah dipost.'
          : Number(nextStatus) === 3
            ? Number(currentStatus) === 1
              ? 'Transaksi membership berhasil di-void dan jurnal reversal sudah dibuat.'
              : 'Transaksi membership berhasil dibatalkan.'
            : 'Status transaksi membership berhasil diperbarui.',
        'success'
      );
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memperbarui status membership', 'error');
    } finally {
      setMembershipStatusUpdatingId('');
    }
  };

  const handleCheckin = async event => {
    event.preventDefault();

    if (!selectedCheckinMemberOption?.value || !selectedCheckinMember) {
      Swal.fire('Warning', 'Pilih member yang akan dibantu check-in.', 'warning');
      return;
    }

    setIsCheckinSubmitting(true);
    const memberName = formatMemberFullName(selectedCheckinMember);

    try {
      const data = await performRequest(
        `${API_BASE_URL}admin/checkin`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            member_id: Number(selectedCheckinMemberOption.value),
            outlet_id: Number(selectedOutlet),
          }),
        },
        'Check-in member gagal'
      );

      window.dispatchEvent(
        new CustomEvent('primefit:checkin-toast', {
          detail: {
            member: memberName,
            status: 'success',
            statusMessage: data.message || 'Check-in berhasil',
          },
        })
      );

      Swal.fire('Success', data.message || 'Check-in berhasil', 'success');
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent('primefit:checkin-toast', {
          detail: {
            member: memberName,
            status: 'failed',
            statusMessage: error.message || 'Check-in gagal',
          },
        })
      );
      Swal.fire('Error', error.message || 'Check-in member gagal', 'error');
    } finally {
      setIsCheckinSubmitting(false);
    }
  };

  const handleClassSale = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await performRequest(
        `${API_BASE_URL}fitness-transaction/create`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: Number(selectedOutlet),
            transaction_type: 'class_sale',
            class_id: Number(classSaleForm.class_id),
            participant_count: Number(classSaleForm.participant_count || 0),
            payment_method: classSaleForm.payment_method,
            notes: classSaleForm.notes.trim(),
          }),
        },
        'Gagal memproses transaksi class'
      );

      Swal.fire('Success', 'Transaksi class berhasil dipost', 'success');
      setClassSaleForm(prev => ({
        ...prev,
        class_id: '',
        participant_count: '1',
        notes: '',
      }));
      await fetchFitnessActivity(selectedOutlet);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memproses transaksi class', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePTSale = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const createResponse = await performRequest(
        `${API_BASE_URL}pt-sale/create`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: Number(selectedOutlet),
            client_id: Number(ptSaleForm.pt_client_id),
            pt_user_id: Number(ptSaleForm.pt_user_id),
            product_id: Number(ptSaleForm.product_id),
            payment_method: ptSaleForm.payment_method,
            discount_amount: Number(ptSaleForm.discount_amount || 0),
            notes: ptSaleForm.notes.trim(),
          }),
        },
        'Gagal membuat PT sale'
      );

      const createdSaleId = createResponse?.data?.id || createResponse?.id;
      if (!createdSaleId) {
        throw new Error('PT sale berhasil dibuat tetapi id tidak ditemukan');
      }

      await performRequest(
        `${API_BASE_URL}pt-sale/mark-paid`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: Number(createdSaleId),
            payment_method: ptSaleForm.payment_method,
          }),
        },
        'PT sale dibuat tetapi gagal ditandai paid'
      );

      Swal.fire('Success', 'PT sale berhasil dibuat dan langsung dipost', 'success');
      setPTSaleForm(prev => ({
        ...prev,
        pt_client_id: '',
        pt_user_id: '',
        product_id: '',
        discount_amount: '',
        notes: '',
      }));
      await Promise.all([fetchPTSales(selectedOutlet), fetchFitnessActivity(selectedOutlet)]);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memproses transaksi PT', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelFitnessTransaction = async item => {
    if (!item?.id) {
      return;
    }

    const confirm = await Swal.fire({
      title: 'Batalkan transaksi ini?',
      text: `${item.class_name || item.pt_name || 'Fitness transaction'}${item.client_name ? ` • ${item.client_name}` : ''}`,
      icon: 'warning',
      showCancelButton: true,
      input: 'text',
      inputPlaceholder: 'Alasan pembatalan',
      confirmButtonText: 'Batalkan',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      await performRequest(
        `${API_BASE_URL}fitness-transaction/cancel`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: Number(item.id),
            notes: confirm.value || '',
          }),
        },
        'Gagal membatalkan transaksi fitness'
      );

      await fetchFitnessActivity(selectedOutlet);
      Swal.fire('Success', 'Transaksi fitness berhasil dibatalkan dan jurnal reversal sudah dibuat.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal membatalkan transaksi fitness', 'error');
    }
  };

  const renderMembershipTab = () => (
    <Form onSubmit={handleMembershipSale}>
      <div className="sales-workspace__form-grid">
        <Form.Group className="sales-workspace__field">
          <Form.Label>Paket membership</Form.Label>
          <Form.Select
            value={membershipForm.membership_id}
            onChange={event =>
              setMembershipForm(prev => ({ ...prev, membership_id: event.target.value }))
            }
            required
          >
            <option value="">Pilih paket</option>
            {memberships.map(item => (
              <option key={item.id} value={item.id}>
                {item.membership_name || item.name} • {formatCurrency(item.price)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {isSelectedMembershipDaily ? (
          <div className="sales-workspace__helper-card sales-workspace__field sales-workspace__field--full">
            <strong>Mode cepat member harian</strong>
            <span>
              Isi jumlah orang yang masuk. Sistem akan membuat transaksi ke member harian default
              tanpa perlu pilih member satu per satu.
            </span>
          </div>
        ) : (
          <Form.Group className="sales-workspace__field sales-workspace__field--full">
            <Form.Label>Member</Form.Label>
            <AsyncSelect
              cacheOptions
              defaultOptions={defaultMemberOptions}
              loadOptions={loadMemberOptions}
              value={selectedMembershipMemberOption}
              onChange={option => {
                setSelectedMembershipMemberOption(option || null);
                setMembershipForm(prev => ({
                  ...prev,
                  member_id: option?.value || '',
                }));
              }}
              isDisabled={!selectedOutlet}
              placeholder={selectedOutlet ? 'Cari nama, HP, email, atau ID member' : 'Pilih outlet lebih dulu'}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? 'Member tidak ditemukan' : 'Ketik nama atau nomor HP member'
              }
              loadingMessage={() => 'Mencari member...'}
              styles={memberSelectStyles}
            />
          </Form.Group>
        )}

        <Form.Group className="sales-workspace__field">
          <Form.Label>Metode pembayaran</Form.Label>
          <Form.Select
            value={membershipForm.payment_method}
            onChange={event =>
              setMembershipForm(prev => ({ ...prev, payment_method: event.target.value }))
            }
            required
          >
            <option value="">Pilih metode bayar</option>
            {paymentAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.id} - {account.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {isSelectedMembershipDaily && (
          <Form.Group className="sales-workspace__field">
            <Form.Label>Jumlah orang</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="100"
              value={membershipForm.quantity}
              onChange={event =>
                setMembershipForm(prev => ({
                  ...prev,
                  quantity: event.target.value,
                }))
              }
              required
            />
          </Form.Group>
        )}
      </div>

      <div className="sales-workspace__footer">
        <div className="sales-workspace__inline-note">
          {isSelectedMembershipDaily
            ? 'Gunakan mode ini untuk input member harian massal. Semua transaksi tetap dibuat pending.'
            : 'Transaksi membership dibuat sebagai pending. Ubah ke Completed agar jurnal dipost dan member bisa check-in.'}
        </div>
        <Button type="submit" variant="success" disabled={isSubmitting}>
          <FaHandHoldingUsd /> {isSubmitting ? 'Memproses...' : isSelectedMembershipDaily ? 'Post Member Harian' : 'Post Membership'}
        </Button>
      </div>
    </Form>
  );

  const renderCheckinTab = () => (
    <Form onSubmit={handleCheckin}>
      <div className="sales-workspace__form-grid">
        <Form.Group className="sales-workspace__field sales-workspace__field--full">
          <Form.Label>Member check-in</Form.Label>
          <AsyncSelect
            cacheOptions
            defaultOptions={defaultMemberOptions}
            loadOptions={loadMemberOptions}
            value={selectedCheckinMemberOption}
            onChange={option => {
              setSelectedCheckinMemberOption(option || null);
            }}
            isDisabled={!selectedOutlet}
            placeholder={selectedOutlet ? 'Cari member untuk dibantu check-in' : 'Pilih outlet lebih dulu'}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? 'Member tidak ditemukan' : 'Ketik nama atau nomor HP member'
            }
            loadingMessage={() => 'Mencari member...'}
            styles={memberSelectStyles}
          />
        </Form.Group>

        <div className="sales-workspace__helper-card sales-workspace__field sales-workspace__field--full">
          <strong>Catatan check-in FO</strong>
          <span>Check-in hanya akan berhasil jika membership terakhir sudah <code>Completed</code> dan belum expired.</span>
          <span>Jika status terakhir masih pending, selesaikan dulu transaksi di tabel history bawah.</span>
        </div>
      </div>

      <div className="sales-workspace__footer">
        <div className="sales-workspace__inline-note">
          Gunakan tab ini untuk validasi cepat tanpa pindah ke menu member.
        </div>
        <Button type="submit" variant="success" disabled={isCheckinSubmitting}>
          <FaSignInAlt /> {isCheckinSubmitting ? 'Memproses...' : 'Check-in Member'}
        </Button>
      </div>
    </Form>
  );

  const renderClassTab = () => (
    <Form onSubmit={handleClassSale}>
      <div className="sales-workspace__form-grid">
        <Form.Group className="sales-workspace__field sales-workspace__field--full">
          <Form.Label>Class aktif</Form.Label>
          <Form.Select
            value={classSaleForm.class_id}
            onChange={event =>
              setClassSaleForm(prev => ({ ...prev, class_id: event.target.value }))
            }
            required
          >
            <option value="">Pilih class</option>
            {classes.map(item => (
              <option key={item.id} value={item.id}>
                {item.class_name} • {item.coach_name || 'Tanpa coach'} • {formatCurrency(item.unit_price)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>Jumlah peserta</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={classSaleForm.participant_count}
            onChange={event =>
              setClassSaleForm(prev => ({ ...prev, participant_count: event.target.value }))
            }
            required
          />
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>Metode pembayaran</Form.Label>
          <Form.Select
            value={classSaleForm.payment_method}
            onChange={event =>
              setClassSaleForm(prev => ({ ...prev, payment_method: event.target.value }))
            }
            required
          >
            <option value="">Pilih metode bayar</option>
            {paymentAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.id} - {account.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field sales-workspace__field--full">
          <Form.Label>Catatan</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={classSaleForm.notes}
            onChange={event =>
              setClassSaleForm(prev => ({ ...prev, notes: event.target.value }))
            }
            placeholder="Contoh: Aerobic pagi, 7 peserta"
          />
        </Form.Group>
      </div>

      <div className="sales-workspace__footer">
        <div className="sales-workspace__inline-note">
          Harga class akan dihitung otomatis dari setup management.
        </div>
        <Button type="submit" variant="success" disabled={isSubmitting}>
          <FaHandHoldingUsd /> {isSubmitting ? 'Memproses...' : 'Post Class Sale'}
        </Button>
      </div>
    </Form>
  );

  const renderPTTab = () => (
    <Form onSubmit={handlePTSale}>
      <div className="sales-workspace__form-grid">
        <Form.Group className="sales-workspace__field">
          <Form.Label>PT</Form.Label>
          <Form.Select
            value={ptSaleForm.pt_user_id}
            onChange={event =>
              setPTSaleForm(prev => ({
                ...prev,
                pt_user_id: event.target.value,
                product_id: '',
              }))
            }
            required
          >
            <option value="">Pilih PT</option>
            {ptUsers
              .filter(item => item.is_active)
              .map(item => (
                <option key={item.id} value={item.id}>
                  {item.pt_name}
                </option>
              ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>PT client</Form.Label>
          <Form.Select
            value={ptSaleForm.pt_client_id}
            onChange={event =>
              setPTSaleForm(prev => ({ ...prev, pt_client_id: event.target.value }))
            }
            required
          >
            <option value="">Pilih client</option>
            {ptClients
              .filter(item => item.is_active)
              .map(item => (
                <option key={item.id} value={item.id}>
                  {item.full_name}{item.member_name ? ` • ${item.member_name}` : ''}
                </option>
              ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>PT product</Form.Label>
          <Form.Select
            value={ptSaleForm.product_id}
            onChange={event =>
              setPTSaleForm(prev => ({ ...prev, product_id: event.target.value }))
            }
            disabled={!ptSaleForm.pt_user_id}
            required
          >
            <option value="">
              {ptSaleForm.pt_user_id ? 'Pilih product' : 'Pilih PT dulu'}
            </option>
            {availablePTProducts.map(item => (
              <option key={item.id} value={item.id}>
                {item.product_name} • {item.session_count} sesi • {formatCurrency(item.price)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>Metode pembayaran</Form.Label>
          <Form.Select
            value={ptSaleForm.payment_method}
            onChange={event =>
              setPTSaleForm(prev => ({ ...prev, payment_method: event.target.value }))
            }
            required
          >
            <option value="">Pilih metode bayar</option>
            {paymentAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.id} - {account.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="sales-workspace__field">
          <Form.Label>Diskon</Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={ptSaleForm.discount_amount}
            onChange={event =>
              setPTSaleForm(prev => ({ ...prev, discount_amount: event.target.value }))
            }
          />
        </Form.Group>

        <Form.Group className="sales-workspace__field sales-workspace__field--full">
          <Form.Label>Catatan</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={ptSaleForm.notes}
            onChange={event =>
              setPTSaleForm(prev => ({ ...prev, notes: event.target.value }))
            }
            placeholder="Contoh: Paket 10 sesi untuk Dika"
          />
        </Form.Group>
      </div>

      <div className="sales-workspace__footer">
        <div className="sales-workspace__inline-note">
          Sales desk PT memakai master client, product, dan sale yang sama dengan halaman PT setup.
        </div>
        <Button type="submit" variant="success" disabled={isSubmitting}>
          <FaHandHoldingUsd /> {isSubmitting ? 'Memproses...' : 'Create + Post PT Sale'}
        </Button>
      </div>
    </Form>
  );

  const previewTitle =
    activeTab === 'membership'
      ? membershipPreview.label
      : activeTab === 'checkin'
        ? selectedCheckinMember
          ? formatMemberFullName(selectedCheckinMember)
          : 'Member belum dipilih'
      : activeTab === 'class'
        ? selectedClass?.class_name || 'Class belum dipilih'
        : selectedPT?.pt_name || 'PT belum dipilih';

  const renderPreview = () => {
    if (activeTab === 'membership') {
      return (
        <>
          <div className="sales-preview__amount">
            {formatCurrency(
              isSelectedMembershipDaily ? membershipPreview.totalAmount : membershipPreview.amount
            )}
          </div>
          <div className="sales-preview__meta">
            <span>Durasi paket</span>
            <strong>{membershipPreview.duration || 0} hari</strong>
          </div>
          <div className="sales-preview__meta">
            <span>{isSelectedMembershipDaily ? 'Jumlah orang' : 'Member'}</span>
            <strong>
              {isSelectedMembershipDaily
                ? `${membershipPreview.quantity} orang`
                : selectedMembershipMember
                  ? formatMemberFullName(selectedMembershipMember)
                  : '-'}
            </strong>
          </div>
          {isSelectedMembershipDaily && (
            <div className="sales-preview__meta">
              <span>Mode</span>
              <strong>Member harian default</strong>
            </div>
          )}
          <div className="sales-preview__meta">
            <span>{isSelectedMembershipDaily ? 'Harga per orang' : 'Harga paket'}</span>
            <strong>{formatCurrency(membershipPreview.amount)}</strong>
          </div>
          <div className="sales-preview__meta">
            <span>Metode bayar</span>
            <strong>{selectedPaymentAccount?.name || membershipForm.payment_method || '-'}</strong>
          </div>
        </>
      );
    }

    if (activeTab === 'checkin') {
      const statusConfig = getMembershipStatusConfig(latestFocusedMemberPayment?.status);

      return (
        <>
          <div className="sales-preview__amount sales-preview__amount--compact">
            {selectedCheckinMember ? formatMemberFullName(selectedCheckinMember) : 'Pilih member'}
          </div>
          <div className="sales-preview__meta">
            <span>Kontak</span>
            <strong>{selectedCheckinMember?.hp || selectedCheckinMember?.email || '-'}</strong>
          </div>
          <div className="sales-preview__meta">
            <span>Membership terakhir</span>
            <strong>{latestFocusedMemberPayment?.membership_name || '-'}</strong>
          </div>
          <div className="sales-preview__meta">
            <span>Status</span>
            <strong className={`sales-status-text ${statusConfig.className}`}>
              {latestFocusedMemberPayment ? statusConfig.label : 'Belum ada transaksi'}
            </strong>
          </div>
          <div className="sales-preview__meta">
            <span>Expired</span>
            <strong>{latestFocusedMemberPayment?.expired ? formatDateTime(latestFocusedMemberPayment.expired) : '-'}</strong>
          </div>
        </>
      );
    }

    if (activeTab === 'class') {
      return (
        <>
          <div className="sales-preview__amount">{formatCurrency(classPreview.grossAmount)}</div>
        </>
      );
    }

    return (
      <>
        <div className="sales-preview__amount">{formatCurrency(ptPreview.grossAmount)}</div>
        <div className="sales-preview__meta">
          <span>Produk</span>
          <strong>{selectedPTProduct?.product_name || '-'}</strong>
        </div>
        <div className="sales-preview__meta">
          <span>Client</span>
          <strong>{selectedPTClient?.full_name || '-'}</strong>
        </div>
        <div className="sales-preview__meta">
          <span>Sesi</span>
          <strong>{ptPreview.sessionCount || 0}</strong>
        </div>
        <div className="sales-preview__meta">
          <span>Diskon</span>
          <strong>{formatCurrency(ptPreview.discountAmount)}</strong>
        </div>
        <div className="sales-preview__meta">
          <span>Metode bayar</span>
          <strong>{selectedPTPaymentAccount?.name || ptSaleForm.payment_method || '-'}</strong>
        </div>
      </>
    );
  };

  const renderActivity = () => {
    if (activeTab === 'membership' || activeTab === 'checkin') {
      return (
        <Table responsive className="mb-0 sales-workspace__table">
          <thead>
            <tr>
              <th>Waktu</th>
              <th>Member</th>
              <th>Paket</th>
              <th>Jumlah</th>
              <th>Metode</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {membershipActivityRows.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4 text-muted">
                  {focusedMemberId
                    ? 'Belum ada history membership pada rentang tanggal ini untuk member yang dipilih.'
                    : 'Belum ada transaksi membership pada rentang tanggal ini.'}
                </td>
              </tr>
            ) : null}
            {membershipActivityRows.map(item => {
              const statusConfig = getMembershipStatusConfig(item.status);

              return (
              <tr key={item.id}>
                <td>{formatDateTime(item.created_at)}</td>
                <td>
                  {item.member_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || '-'}
                  {item.outlet ? <div className="text-muted small">{item.outlet}</div> : null}
                </td>
                <td>{item.membership_name || '-'}</td>
                <td>{formatCurrency(item.amount)}</td>
                <td>{item.payment_method || '-'}</td>
                <td>
                  <span className={`sales-status-pill ${statusConfig.className}`}>
                    {statusConfig.label}
                  </span>
                </td>
                <td>
                  <Form.Select
                    className={`sales-workspace__status-select ${statusConfig.className}`}
                    value={Number(item.status || 0)}
                    onChange={event =>
                      handleMembershipStatusChange(
                        item.id,
                        item.status,
                        parseInt(event.target.value, 10)
                      )
                    }
                    disabled={
                      Number(item.status || 0) === 3 ||
                      membershipStatusUpdatingId === String(item.id)
                    }
                  >
                    {Number(item.status || 0) === 1 ? (
                      <>
                        <option value={1}>Completed</option>
                        <option value={3}>Void</option>
                      </>
                    ) : (
                      <>
                        <option value={0}>Pending</option>
                        <option value={1}>Completed</option>
                        <option value={2}>Failed</option>
                        <option value={3}>Cancelled</option>
                      </>
                    )}
                  </Form.Select>
                </td>
              </tr>
              );
            })}
          </tbody>
        </Table>
      );
    }

    const filteredTransactions = recentFitnessActivityRows.filter(item =>
      isClassFitnessTransaction(item) || isPTFitnessTransaction(item)
    );

    return (
      <Table responsive className="mb-0 sales-workspace__table">
        <thead>
          <tr>
            <th>Waktu</th>
            <th>Channel</th>
            <th>Detail</th>
            <th>Net</th>
            <th>Status</th>
            {canViewShareBreakdown ? <th>Coach</th> : null}
            {canViewShareBreakdown ? <th>Gym</th> : null}
            <th>Dibayar</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.length === 0 ? (
            <tr>
              <td colSpan={canViewShareBreakdown ? 8 : 6} className="text-center py-4 text-muted">
                Belum ada transaksi class atau PT pada rentang tanggal ini.
              </td>
            </tr>
          ) : null}
          {filteredTransactions.map(item => {
            const linkedPTSale = item.linked_pt_sale || null;
            const isClassSale = isClassFitnessTransaction(item);
            const statusConfig = linkedPTSale
              ? getPTSaleStatusConfig(linkedPTSale.payment_status)
              : getFitnessTransactionStatusConfig(item.status);
            const channelLabel = getFitnessTransactionChannelLabel(item);
            const transactionAmount = linkedPTSale?.net_amount || item.gross_amount;
            const paidAtLabel = linkedPTSale?.paid_at || item.created_at;
            const detailPrimary = isClassSale
              ? item.class_name || '-'
              : linkedPTSale?.product_name || item.pt_name || '-';
            const detailSecondary = isClassSale
              ? `${item.coach_name || 'Tanpa coach'}${Number(item.participant_count || 0) > 0 ? ` • ${item.participant_count} peserta` : ''}`
              : [item.client_name, item.pt_name].filter(Boolean).join(' • ') || '-';
            const detailMeta = linkedPTSale?.invoice_no
              ? `Invoice ${linkedPTSale.invoice_no}`
              : item.payment_method || '-';
            return (
            <tr key={item.id}>
              <td>{formatDateTime(item.created_at)}</td>
              <td>
                <strong>{channelLabel}</strong>
                <div className="text-muted small">{detailMeta}</div>
              </td>
              <td>
                <strong>{detailPrimary}</strong>
                <div className="text-muted small">{detailSecondary}</div>
              </td>
              <td>{formatCurrency(transactionAmount)}</td>
              <td>
                <span className={`sales-status-pill ${statusConfig.className}`}>
                  {statusConfig.label}
                </span>
                {linkedPTSale?.contract_status ? (
                  <div className="text-muted small mt-1">Contract: {linkedPTSale.contract_status}</div>
                ) : null}
              </td>
              {canViewShareBreakdown ? <td>{formatCurrency(item.coach_share_amount)}</td> : null}
              {canViewShareBreakdown ? <td>{formatCurrency(item.gym_share_amount)}</td> : null}
              <td>{formatDateTime(paidAtLabel)}</td>
              <td>
                {String(item.status || '').toLowerCase() !== 'cancelled' ? (
                  <Button size="sm" variant="outline-danger" onClick={() => handleCancelFitnessTransaction(item)}>
                    <FaBan />
                  </Button>
                ) : (
                  <span className="text-muted small">-</span>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4 pb-5">
        <AdminPageHeader
          icon={FaCashRegister}
          eyebrow="Front desk workspace"
          title="Front Desk"
          description="Satu layar front office untuk membership, check-in, class, dan PT tanpa pindah menu."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Front Desk' },
          ]}
          actions={
            <div className="d-flex flex-wrap gap-2">
              {!isStaffUser && canUseMembershipSales ? (
                <Button as={Link} to="/membership-payment" variant="outline-dark">
                  <FaArrowRight /> Membership history
                </Button>
              ) : null}
            </div>
          }
        />

        <Card className="sales-workspace__hero">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col xl={5}>
                <div className="sales-workspace__hero-copy">
                  <span className="sales-workspace__eyebrow">Active outlet</span>
                  <h2>{selectedOutletName}</h2>
                  <p>
                    Workspace ini hanya menampilkan flow transaksi yang aktif untuk akun dan outlet
                    saat ini.
                  </p>
                </div>
              </Col>
              <Col xl={4}>
                <Form.Group>
                  <Form.Label>Pilih outlet</Form.Label>
                  <Form.Select
                    value={selectedOutlet}
                    onChange={event => setSelectedOutlet(event.target.value)}
                    disabled={isScopedActor}
                  >
                    <option value="">Pilih outlet</option>
                    {outlets.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.outlet_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xl={3}>
                <div className="sales-workspace__flow-tags">
                  {availableTabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <Badge key={tab.key} bg="light" text="dark" className="sales-workspace__flow-badge">
                        <Icon /> {tab.label}
                      </Badge>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-3 sales-workspace__stats">
          {summaryCards.map(card => (
            <Col md={4} key={card.label}>
              <Card className="sales-workspace__stat-card">
                <Card.Body>
                  <span>{card.label}</span>
                  <strong>{card.value}</strong>
                  <small>{card.note}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-4 mt-1">
          <Col xl={8}>
            <Card className="sales-workspace__main-card">
              <Card.Body>
                <div className="sales-workspace__tabs">
                  {availableTabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;

                    return (
                      <button
                        key={tab.key}
                        type="button"
                        className={`sales-workspace__tab ${isActive ? 'is-active' : ''}`}
                        onClick={() => setSearchParams({ tab: tab.key })}
                      >
                        <span className="sales-workspace__tab-icon">
                          <Icon />
                        </span>
                        <span>
                          <strong>{tab.label}</strong>
                          <small>{tab.description}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="sales-workspace__panel">
                  {isBootstrapping ? (
                    <div className="sales-workspace__empty-state">Memuat data transaksi...</div>
                  ) : null}

                  {!isBootstrapping && activeTab === 'membership' ? renderMembershipTab() : null}
                  {!isBootstrapping && activeTab === 'checkin' ? renderCheckinTab() : null}
                  {!isBootstrapping && activeTab === 'class' ? renderClassTab() : null}
                  {!isBootstrapping && activeTab === 'pt' ? renderPTTab() : null}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="sales-preview">
              <Card.Body>
                <div className="sales-preview__label">Preview transaksi</div>
                <h3>{previewTitle}</h3>
                {renderPreview()}
              </Card.Body>
            </Card>

            <Card className="sales-workspace__side-card">
              <Card.Body>
                <div className="sales-workspace__side-title">
                  <FaLayerGroup />
                  <span>Operator notes</span>
                </div>
                <ul className="sales-workspace__notes">
                  <li>Outlet terkunci otomatis untuk akun outlet dan karyawan.</li>
                  <li>Staf hanya melihat class aktif dan PT aktif.</li>
                  <li>Transaksi membership dimulai dari pending dan wajib diubah ke Completed agar jurnal dipost.</li>
                  <li>Class dan PT tetap langsung mencatat jurnal saat transaksi berhasil dipost.</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="sales-workspace__activity-card mt-4">
          <Card.Body>
            <div className="sales-workspace__activity-head">
              <div>
                <span className="sales-workspace__eyebrow">Recent activity</span>
                <h3>
                  {activeTab === 'membership' || activeTab === 'checkin'
                    ? focusedMemberId
                      ? 'Membership history'
                      : 'Membership transactions'
                    : 'Fitness transactions'}
                </h3>
              </div>
              <Badge bg="light" text="dark">
                {focusedMemberId && (selectedMembershipMember || selectedCheckinMember)
                  ? formatMemberFullName(selectedMembershipMember || selectedCheckinMember)
                  : selectedOutletName}
              </Badge>
            </div>
            <div className="sales-workspace__activity-tools">
              <div className="sales-workspace__activity-filters">
                <div className="sales-workspace__activity-filter-field">
                  <span>Dari</span>
                  <Form.Control
                    type="date"
                    value={activityDateRange.dateFrom}
                    max={activityDateRange.dateTo || undefined}
                    onChange={event => handleActivityDateChange('dateFrom', event.target.value)}
                  />
                </div>
                <div className="sales-workspace__activity-filter-field">
                  <span>Sampai</span>
                  <Form.Control
                    type="date"
                    value={activityDateRange.dateTo}
                    min={activityDateRange.dateFrom || undefined}
                    onChange={event => handleActivityDateChange('dateTo', event.target.value)}
                  />
                </div>
                {!isActivityDateRangeToday ? (
                  <Button variant="outline-secondary" onClick={resetActivityDateRangeToToday}>
                    Hari ini
                  </Button>
                ) : null}
              </div>
              <div className="sales-workspace__activity-note">
                {isStaffUser
                  ? 'Akun staf hanya melihat transaksi yang dibuat oleh staf ini.'
                  : 'Gunakan custom range untuk audit transaksi per tanggal.'}
              </div>
            </div>
            {renderActivity()}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default FitnessTransactionsManager;
